/**
 * PerformanceTrail — Full instrumentation layer for NeuroPsyMap
 * Ported from AstroMonix PerformanceTrail pattern:
 * - Span timing (start/end) with high-precision monotonic clock
 * - Per-label aggregation (min/max/avg/last) — always on, negligible overhead
 * - Budget-based over-budget detection with ⚠️ styling
 * - Per-label throttle to prevent console flooding
 * - Error enrichment with structured key=value context
 * - FPS tracking via per-frame tick()
 * - Ring-buffer history (capped at 200)
 * - Zero-cost when disabled: `_pt && _pt.start(...)` short-circuits
 */

export interface SpanEntry { start: number; threshold: number }
export interface HistoryEntry { label: string; elapsed: number; timestamp: number; overBudget: boolean }
export interface AggregateStats { count: number; total: number; min: number; max: number; last: number }
export interface ErrorContext { [key: string]: unknown }

export interface PerfTrailDump {
  aggregates: Record<string, AggregateStats>;
  history: HistoryEntry[];
  activeSpans: string[];
  budgets: Record<string, number>;
  timestamp: number;
}

export interface PerformanceMetrics {
  fps: number; frameTime: number; jsHeapSize: number; jsHeapLimit: number;
  totalNodes: number; totalEdges: number; activeAudioNodes: number;
  spatialHashSize: number; lastSaveTime: number; simulationStepTime: number; renderTime: number;
}

export class PerformanceTrail {
  private _enabled = false;
  private _spans: Record<string, SpanEntry> = {};
  private _aggregates: Record<string, AggregateStats> = {};
  private _budgets: Record<string, number> = {};
  private _history: HistoryEntry[] = [];
  private readonly _maxHistory = 200;
  private _lastLogged: Record<string, number> = {};
  private _logThrottleMs: Record<string, number> = { '_default': 500, 'animation-frame': 3000, 'fps': 5000, '__overbudget__': 1000 };
  private _frameCount = 0;
  private _frameWindowStart = 0;
  private _frameFPS = 0;

  // Panel overlay
  private panel: HTMLElement | null = null;
  private panelVisible = false;
  private metricsProviders: Array<() => Partial<PerformanceMetrics>> = [];
  private currentMetrics: PerformanceMetrics = this._emptyMetrics();

  constructor() {
    this._frameWindowStart = performance.now();
    this._tryAutoEnable();
  }

  // ── Master Gate ──
  enable(): void { this._enabled = true; }
  disable(): void { this._enabled = false; }
  get enabled(): boolean { return this._enabled; }

  // ── Budgets ──
  budget(label: string, ms: number): void { this._budgets[label] = ms; }
  setThrottle(label: string, ms: number): void { this._logThrottleMs[label] = ms; }

  // ── Spans ──
  start(label: string): void {
    if (!this._enabled) return;
    this._spans[label] = { start: performance.now(), threshold: this._budgets[label] ?? Infinity };
  }

  end(label: string): number {
    const span = this._spans[label];
    if (!span) return 0;
    delete this._spans[label];

    const elapsed = performance.now() - span.start;
    const overBudget = elapsed > span.threshold;

    let agg = this._aggregates[label];
    if (!agg) { agg = { count: 0, total: 0, min: Infinity, max: 0, last: 0 }; this._aggregates[label] = agg; }
    agg.count++; agg.total += elapsed;
    if (elapsed < agg.min) agg.min = elapsed;
    if (elapsed > agg.max) agg.max = elapsed;
    agg.last = elapsed;

    this._history.push({ label, elapsed, timestamp: Date.now(), overBudget });
    if (this._history.length > this._maxHistory) this._history.shift();

    if (this._shouldLog(label, overBudget)) this._logSpan(label, elapsed, span.threshold, overBudget, agg);
    return elapsed;
  }

  wrap<T>(label: string, fn: () => T): T {
    this.start(label);
    try {
      const result = fn();
      if (result instanceof Promise) return result.then(v => { this.end(label); return v; }, e => { this.end(label); throw e; }) as unknown as T;
      return result;
    } finally { this.end(label); }
  }

  // ── Error Enrichment ──
  error(source: string, message: string, context?: ErrorContext): void {
    if (!this._enabled) return;
    const ctx = context ? ' | ' + Object.entries(context).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(', ') : '';
    console.error(`%c❌ %c[${source}] %c${message}%c${ctx}`, 'color:#f44;', 'color:#f0a;font-weight:bold;', 'color:#fff;font-weight:bold;', 'color:#999;');
  }

  checkpoint(label: string, data?: Record<string, unknown>): void {
    if (!this._enabled) return;
    const ctx = data ? ' | ' + Object.entries(data).map(([k, v]) => `${k}=${String(v)}`).join(', ') : '';
    console.error(`%c◆ %c${label}%c${ctx}`, 'color:#c9f;', 'color:#fff;', 'color:#999;');
  }

  // ── FPS ──
  tick(): number {
    if (!this._enabled) return 0;
    this._frameCount++;
    const now = performance.now();
    if (now - this._frameWindowStart >= 1000) {
      this._frameFPS = Math.round((this._frameCount * 1000) / Math.max(1, now - this._frameWindowStart));
      this._frameCount = 0; this._frameWindowStart = now;
      if (this._frameFPS < 45 && this._shouldLog('fps', false))
        console.error(`%c○ %cFPS: ${this._frameFPS} %c(low)`, 'color:#f66;', 'color:#fff;font-weight:bold;', 'color:#f90;');
    }
    if (this.panelVisible) this._updatePanel();
    return this._frameFPS;
  }

  fps(): number { return this._frameFPS; }

  // ── Reporting ──
  report(): Array<{ label: string; count: number; last_ms: number; avg_ms: number; min_ms: number; max_ms: number; total_ms: number }> | null {
    const entries = Object.entries(this._aggregates);
    if (entries.length === 0) return null;
    const rows = entries.map(([label, agg]) => ({
      label, count: agg.count,
      last_ms: Math.round(agg.last * 100) / 100,
      avg_ms: Math.round((agg.total / agg.count) * 100) / 100,
      min_ms: Math.round(agg.min * 100) / 100,
      max_ms: Math.round(agg.max * 100) / 100,
      total_ms: Math.round(agg.total * 100) / 100,
    }));
    console.table(rows);
    return rows;
  }

  dump(): PerfTrailDump {
    const p: PerfTrailDump = { aggregates: { ...this._aggregates }, history: this._history.slice(-100), activeSpans: Object.keys(this._spans), budgets: { ...this._budgets }, timestamp: Date.now() };
    console.log(JSON.stringify(p, null, 2));
    navigator.clipboard?.writeText(JSON.stringify(p, null, 2)).catch(() => {});
    return p;
  }

  reset(): void {
    this._spans = {}; this._aggregates = {}; this._history = []; this._lastLogged = {};
    this._frameCount = 0; this._frameFPS = 0; this._frameWindowStart = performance.now();
  }

  // ── Metrics ──
  registerMetricsProvider(p: () => Partial<PerformanceMetrics>): void { this.metricsProviders.push(p); }
  updateMetrics(partial: Partial<PerformanceMetrics>): void { Object.assign(this.currentMetrics, partial); }
  getMetrics(): PerformanceMetrics {
    for (const p of this.metricsProviders) { try { Object.assign(this.currentMetrics, p()); } catch { /* skip */ } }
    return { ...this.currentMetrics };
  }
  getAverageFps(): number { return this._frameFPS; }

  // ── Panel ──
  togglePanel(): void { this.panelVisible = !this.panelVisible; this.panelVisible ? this._showPanel() : this._hidePanel(); }
  showPanel(): void { this.panelVisible = true; this._showPanel(); }
  hidePanel(): void { this.panelVisible = false; this._hidePanel(); }
  destroy(): void { if (this.panel) { this.panel.remove(); this.panel = null; } }

  // ── Private ──
  private _shouldLog(label: string, overBudget: boolean): boolean {
    if (!this._enabled) return false;
    const now = Date.now();
    const key = overBudget ? '__overbudget__' : label;
    const interval = overBudget ? (this._logThrottleMs['__overbudget__'] ?? 1000) : (this._logThrottleMs[label] ?? this._logThrottleMs['_default'] ?? 500);
    const last = this._lastLogged[key] ?? 0;
    if (now - last < interval) return false;
    this._lastLogged[key] = now;
    return true;
  }

  private _logSpan(label: string, elapsed: number, threshold: number, overBudget: boolean, agg: AggregateStats): void {
    const icon = overBudget ? '⚠️' : '⏱️';
    const color = overBudget ? 'color:#f90;' : 'color:#6cf;';
    const stats = agg.count > 1 ? ` | avg:${(agg.total / agg.count).toFixed(2)}ms min:${agg.min.toFixed(2)}ms max:${agg.max.toFixed(2)}ms` : '';
    console.error(`%c${icon} %c${label}: ${elapsed.toFixed(2)}ms %c/ ${threshold < Infinity ? threshold.toFixed(0) : '∞'}ms%c${stats}`, color, 'color:#fff;font-weight:bold;', 'color:#999;', 'color:#888;');
  }

  private _tryAutoEnable(): void {
    try {
      if (typeof window === 'undefined') return;
      const host = window.location?.hostname ?? '';
      if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) { this.enable(); return; }
      const params = new URLSearchParams(window.location?.search ?? '');
      if (params.get('debug_perf') === '1' || params.get('debug') === '1') { this.enable(); return; }
      if (params.get('debug_perf') === '0') { this.disable(); return; }
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem('neuropsymap-perf-trail');
        if (stored === '1') this.enable();
        else if (stored === '0') this.disable();
      }
    } catch { /* ignore */ }
  }

  private _emptyMetrics(): PerformanceMetrics {
    return { fps: 0, frameTime: 0, jsHeapSize: 0, jsHeapLimit: 0, totalNodes: 0, totalEdges: 0, activeAudioNodes: 0, spatialHashSize: 0, lastSaveTime: 0, simulationStepTime: 0, renderTime: 0 };
  }

  private _updatePanel(): void {
    if (!this.panel) return;
    const m = this.getMetrics(); m.fps = this._frameFPS;
    const e = (id: string) => this.panel!.querySelector(id);
    const set = (id: string, t: string, c?: string) => { const el = e(id); if (el) { el.textContent = t; if (c) (el as HTMLElement).style.color = c; } };
    set('#perf-fps', `${this._frameFPS}`, this._frameFPS >= 55 ? '#10b981' : this._frameFPS >= 30 ? '#f59e0b' : '#ef4444');
    set('#perf-frametime', `${(m.frameTime ?? 0).toFixed(2)}ms`);
    set('#perf-memory', `${((m.jsHeapSize ?? 0) / 1048576).toFixed(1)}/${((m.jsHeapLimit ?? 0) / 1048576).toFixed(1)}MB`);
    set('#perf-step', `${(m.simulationStepTime ?? 0).toFixed(2)}ms`);
    set('#perf-render', `${(m.renderTime ?? 0).toFixed(2)}ms`);
    set('#perf-counts', `${m.totalNodes}n/${m.totalEdges}e`);
    set('#perf-audio', `${m.activeAudioNodes}`);
    set('#perf-save', (m.lastSaveTime ?? 0) > 0 ? `${(m.lastSaveTime ?? 0).toFixed(2)}ms` : 'N/A');
    set('#perf-spans', `${Object.keys(this._aggregates).length}spans`);
    set('#perf-overbudget', `${this._history.filter(h => h.overBudget).length}⚠️`, '#f90');
  }

  private _showPanel(): void {
    if (!this.panel) {
      this.panel = document.createElement('div');
      this.panel.id = 'perf-trail-panel';
      this.panel.style.cssText = 'position:fixed;top:8px;right:8px;z-index:10000;background:rgba(17,24,39,0.92);backdrop-filter:blur(6px);border:1px solid #374151;border-radius:8px;padding:10px 14px;font-family:Consolas,Monaco,monospace;font-size:11px;color:#d1d5db;min-width:200px;box-shadow:0 4px 16px rgba(0,0,0,0.4);user-select:none;line-height:1.6;';
      this.panel.innerHTML = `<div style="display:flex;justify-content:space-between;margin-bottom:6px;border-bottom:1px solid #374151;padding-bottom:4px;"><span style="color:#818cf8;font-weight:bold;">⚡ PERF TRAIL</span><span style="color:#6b7280;cursor:pointer;">✕</span></div><div>FPS: <span id="perf-fps">0</span></div><div>Frame: <span id="perf-frametime">0ms</span></div><div>Mem: <span id="perf-memory">0MB</span></div><div style="margin-top:4px;border-top:1px solid #374151;padding-top:2px;">Step: <span id="perf-step">0ms</span></div><div>Render: <span id="perf-render">0ms</span></div><div>Net: <span id="perf-counts">0</span></div><div>Audio: <span id="perf-audio">0</span></div><div>Save: <span id="perf-save">N/A</span></div><div>Spans: <span id="perf-spans">0</span></div><div>Over: <span id="perf-overbudget">0</span></div>`;
      document.body.appendChild(this.panel);
    }
    this.panel.style.display = 'block';
    this._updatePanel();
  }

  private _hidePanel(): void { if (this.panel) this.panel.style.display = 'none'; }
}

// Singleton
let _inst: PerformanceTrail | null = null;
export function getPerformanceTrail(): PerformanceTrail { if (!_inst) _inst = new PerformanceTrail(); return _inst; }
export function getPerformanceMonitor(): PerformanceTrail { return getPerformanceTrail(); }
